export const INBOX_STAGE_LABELS = {
  new: { label: 'Ny', className: 'inquiry-workflow-new' },
  in_progress: { label: 'Pågår', className: 'inquiry-workflow-in-progress' },
  resolved: { label: 'Løst', className: 'inquiry-workflow-resolved' },
};

const INBOX_SOURCE_LABELS = new Map([
  ['chat', 'Chat'],
  ['python_chat_api.submit_inquiry', 'Chat'],
  ['python_chat_api.submit_inquiry.py', 'Chat'],
]);

export const EMPTY_TOTALS = {
  total: 0,
  unread: 0,
  new: 0,
  in_progress: 0,
  resolved: 0,
  unassigned: 0,
};

export const PAGE_SIZE = 20;
export const INBOX_CASEWORK_STORAGE_PREFIX = 'casework__';
export const INBOX_ATTRIBUTE_STORAGE_PREFIX = 'inquiry__';

export const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('nb-NO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatRelativeTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'akkurat nå';
  if (diffMinutes < 60) return `${diffMinutes} min siden`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} t siden`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} d siden`;

  return formatDate(value);
};

export const ensureObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

export const stringifyValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nei';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const locationLabel = value.address || value.formatted_address || value.formattedAddress;
    if (locationLabel) return String(locationLabel);
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  }
  return String(value);
};

export const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nei';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const locationLabel = value.address || value.formatted_address || value.formattedAddress;
    if (locationLabel) return String(locationLabel);
    try {
      return JSON.stringify(value, null, 2);
    } catch (_error) {
      return String(value);
    }
  }
  if (typeof value === 'string') {
    const isoDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.exec(value);
    if (isoDate) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString('nb-NO', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }
    return value;
  }
  return String(value);
};

export const getStageMeta = (stage) => INBOX_STAGE_LABELS[stage] || INBOX_STAGE_LABELS.new;

export const getProgressionCustomStatuses = (progression) => (
  Array.isArray(progression?.statuses)
    ? progression.statuses.filter((status) => status?.key && status?.label)
    : []
);

export const getProgressionCustomStatusClassName = (status) => {
  const color = String(status?.color || '').trim().toLowerCase();
  if (['red', 'orange', 'blue', 'green'].includes(color)) {
    return `inquiry-workflow-custom-${color}`;
  }
  return 'inquiry-workflow-resolved';
};

export const getProgressionPercent = (progression) => {
  const value = Number(progression?.progress_percent);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : null;
};

export const PROGRESSION_COMPLETE_STATUSES = new Set(['done', 'approved', 'not_approved']);

export const getProgressionStepCompletion = (stepOrStatus) => {
  if (stepOrStatus && typeof stepOrStatus === 'object') {
    const configuredCompletion = Number(stepOrStatus.status_completion);
    if (Number.isFinite(configuredCompletion)) {
      return Math.max(0, Math.min(1, configuredCompletion > 1 ? configuredCompletion / 100 : configuredCompletion));
    }
    return getProgressionStepCompletion(stepOrStatus.status);
  }
  const status = stepOrStatus;
  if (PROGRESSION_COMPLETE_STATUSES.has(status)) return 1;
  if (['planned', 'in_progress'].includes(status)) return 0.5;
  return 0;
};

export const isProgressionStepCompleteStatus = (stepOrStatus) => getProgressionStepCompletion(stepOrStatus) >= 1;

const STEP_ITEM_TYPES = ['checkbox', 'appointment', 'log'];

export const getProgressionStepType = (step) => {
  const type = String(step?.type || '').trim().toLowerCase();
  if (type === 'task') return 'appointment';

  if (type === 'step') {
    if (Array.isArray(step?.logFields) && step.logFields.length > 0) return 'log';
    if (Array.isArray(step?.log_fields) && step.log_fields.length > 0) return 'log';
    if (Array.isArray(step?.fields) && step.fields.length > 0) return 'log';
    if (Array.isArray(step?.tasks) && step.tasks.length > 0) return 'checkbox';
    if (Array.isArray(step?.events) && step.events.length > 0) return 'appointment';
    return 'appointment';
  }

  return STEP_ITEM_TYPES.includes(type) ? type : 'appointment';
};

export const getProgressionStepStatusLabel = (stepOrStatus, maybeStatus = undefined) => {
  const step = typeof stepOrStatus === 'object' ? stepOrStatus : null;
  const status = step ? step?.status : stepOrStatus;
  if (step?.status_label) return step.status_label;
  if (status === 'approved') return 'Godkjent';
  if (status === 'not_approved') return 'Ikke godkjent';
  if (status === 'in_progress') return 'Pågår';
  if (status === 'done') return 'Ferdig';
  if (status === 'planned') return 'Planlagt';
  return maybeStatus || 'Ikke startet';
};

export const getProgressionStepTone = (stepOrStatus) => {
  const status = stepOrStatus && typeof stepOrStatus === 'object' ? stepOrStatus.status : stepOrStatus;
  const configuredTone = stepOrStatus && typeof stepOrStatus === 'object' ? stepOrStatus.status_tone : null;
  const usesConfiguredTaskState = stepOrStatus
    && typeof stepOrStatus === 'object'
    && stepOrStatus.type === 'checkbox'
    && (stepOrStatus.task_state_key || stepOrStatus.metadata?.task_state_key);
  if (usesConfiguredTaskState && ['red', 'orange', 'blue', 'green'].includes(configuredTone)) return configuredTone;
  if (status === 'not_approved') return 'not-approved';
  if (['done', 'approved'].includes(status)) return 'done';
  if (['planned', 'in_progress'].includes(status)) return 'planned';
  return 'todo';
};

export const getTaskStepStateOptions = (step) => {
  const taskStates = Array.isArray(step?.taskStates || step?.task_states)
    ? (step.taskStates || step.task_states)
    : [];
  return taskStates.map((state, index) => ({
      value: state.key || state.id || state.value || state.status || `status_${index + 1}`,
      label: state.label || state.name || `Status ${index + 1}`,
      description: `${Number(state.completion || 0)}% fullført`,
      completion: Number(state.completion || 0),
      color: state.color || state.tone || 'blue',
    }));
};

export const getUpcomingProgressionStep = (progression) => {
  if (!progression?.configured || !Array.isArray(progression.steps)) return null;
  return progression.steps.find((step) => !isProgressionStepCompleteStatus(step)) || null;
};

export const getProgressionLabel = (progression) => {
  if (!progression?.configured) return 'Ikke satt';
  return getUpcomingProgressionStep(progression)?.label || progression.current_step?.label || progression.stage_label || 'Ny';
};

export const getComputedStageMeta = (stage, progression) => {
  if (progression?.custom_status?.label) {
    return {
      ...getStageMeta('resolved'),
      label: progression.custom_status.label,
      className: getProgressionCustomStatusClassName(progression.custom_status),
    };
  }
  const stageMeta = getStageMeta(stage);
  const progressPercent = getProgressionPercent(progression);
  const upcomingStep = getUpcomingProgressionStep(progression);
  if (progression?.configured) {
    if (progressPercent >= 100) {
      return getStageMeta('resolved');
    }
    if (progressPercent > 0 || progression?.current_step?.label) {
      return {
        ...getStageMeta('in_progress'),
        label: upcomingStep?.label || progression.current_step?.label || 'Pågår',
        className: 'inquiry-workflow-in-progress',
      };
    }
    return getStageMeta('new');
  }
  if (
    stage === 'in_progress'
    && progression?.current_step?.label
  ) {
    return {
      ...getStageMeta('in_progress'),
      label: upcomingStep?.label || progression.current_step?.label || 'Pågår',
      className: 'inquiry-workflow-in-progress',
    };
  }
  return stageMeta;
};

export const formatInboxSourceLabel = (value) => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) return '—';
  return INBOX_SOURCE_LABELS.get(normalized.toLowerCase()) || normalized;
};

export const getActivityDescription = (entry) => {
  if (!entry) return 'Oppdatering';

  if (entry.event_type === 'created') {
    return 'Henvendelsen ble registrert';
  }

  if (entry.event_type === 'stage_changed') {
    const from = getStageMeta(entry.metadata?.from).label || 'ukjent';
    const to = getStageMeta(entry.metadata?.to).label || 'ukjent';
    return `Status endret fra ${from} til ${to}`;
  }

  if (entry.event_type === 'assignment_changed') {
    const from = entry.metadata?.from?.display_name || entry.metadata?.from?.email || 'ingen';
    const to = entry.metadata?.to?.display_name || entry.metadata?.to?.email || 'ingen';
    return `Ansvarlig endret fra ${from} til ${to}`;
  }

  if (entry.event_type === 'marked_read') {
    return 'Markert som lest';
  }

  if (entry.event_type === 'marked_unread') {
    return 'Markert som ulest';
  }

  if (entry.event_type === 'note_added') {
    return 'Internt notat lagt til';
  }

  if (entry.event_type === 'casework_updated') {
    const type = entry.metadata?.type || '';
    if (type === 'product_link') {
      if (entry.metadata?.to_product_name) {
        return `Produkt koblet til ${entry.metadata.to_product_name}`;
      }
      return 'Produktkobling oppdatert';
    }
    if (type === 'field_values') {
      const fieldLabels = Array.isArray(entry.metadata?.fields) ? entry.metadata.fields.filter(Boolean) : [];
      if (fieldLabels.length > 0) {
        return `Attributter oppdatert: ${fieldLabels.join(', ')}`;
      }
      return 'Attributter oppdatert';
    }
    return 'Intern oppfølging oppdatert';
  }

  if (entry.event_type === 'progression_updated') {
    const stepLabel = entry.metadata?.step_label || entry.metadata?.step_key;
    if (entry.metadata?.type === 'log_created') return `Logg lagt til i ${stepLabel || 'kundeløp'}`;
    if (entry.metadata?.type === 'log_updated') return `Logg oppdatert i ${stepLabel || 'kundeløp'}`;
    if (entry.metadata?.type === 'log_deleted') return `Logg slettet fra ${stepLabel || 'kundeløp'}`;
    if (entry.metadata?.status === 'approved') return `${stepLabel || 'Kundeløp-steg'} godkjent`;
    if (entry.metadata?.status === 'not_approved') return `${stepLabel || 'Kundeløp-steg'} ikke godkjent`;
    if (entry.metadata?.status === 'in_progress') return `${stepLabel || 'Kundeløp-steg'} startet`;
    if (entry.metadata?.status === 'done') return `${stepLabel || 'Kundeløp-steg'} fullført`;
    if (entry.metadata?.status === 'planned') return `${stepLabel || 'Kundeløp-steg'} planlagt`;
    return `${stepLabel || 'Kundeløp'} oppdatert`;
  }

  if (entry.event_type === 'custom_status_changed') {
    const from = entry.metadata?.from?.label || 'kundeløp';
    const to = entry.metadata?.to?.label || 'kundeløp';
    return `Sluttstatus endret fra ${from} til ${to}`;
  }

  return 'Oppdatering';
};

export const getActivitySeverity = (entry) => {
  if (!entry) return 'default';
  if (entry.event_type === 'note_added' || entry.event_type === 'assignment_changed') {
    return 'warning';
  }
  if (entry.event_type === 'marked_read') {
    return 'success';
  }
  if (entry.event_type === 'marked_unread') {
    return 'danger';
  }
  if (entry.event_type === 'casework_updated') {
    return 'warning';
  }
  if (entry.event_type === 'progression_updated') {
    if (entry.metadata?.status === 'done' || entry.metadata?.type === 'log_created') return 'success';
    if (entry.metadata?.status === 'planned') return 'warning';
    return 'default';
  }
  if (entry.event_type === 'custom_status_changed') {
    return entry.metadata?.to ? 'success' : 'warning';
  }
  if (entry.event_type === 'stage_changed') {
    if (entry.metadata?.to === 'resolved') return 'success';
    if (entry.metadata?.to === 'in_progress') return 'warning';
  }
  return 'default';
};
